import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

interface VerticalOnboardingProps {
  partnerId: string;
  onComplete: () => void;
}

const VERTICALS = [
  {
    id: 'flooring',
    icon: '🪵',
    label: 'Vloeren & interieurbouw',
    description: 'PVC, parket, visgraat, egaline',
    compiled_prompt: `Je bent een professionele assistent voor een vloerleggerbedrijf. Je helpt klanten met vragen over PVC vloeren, parket, visgraat, betonlook en ondervloeren. Je bent direct, eerlijk en vakkundig.

Je vraagt altijd naar oppervlakte en gewenst vloertype voordat je een prijs noemt. Je stuurt offertes alleen als je het e-mailadres, de oppervlakte en het gewenste product hebt. Je plant bezoeken alleen als de klant een specifieke vraag heeft die ter plaatse beantwoord moet worden.

Je reageert snel en bondig. WhatsApp berichten, geen e-mailstijl. Je sluit nooit af met 'Met vriendelijke groet'.`,
    operator_identity: 'Vloerleggerbedrijf in Nederland. Specialisatie in PVC en parket vloeren.',
    client_reading: 'Klanten die vragen stellen over vloeren zijn vaak bezig met een verbouwing of verhuizing. Ze willen snel weten of jij hun probleem kunt oplossen en voor welke prijs. Wees direct en concreet.',
    signature_approach: 'Direct, deskundig, toegankelijk. Geen jargon tenzij de klant het zelf gebruikt. Korte zinnen.',
    hard_constraints: 'Nooit een prijs noemen zonder oppervlakte te kennen. Nooit beloven dat iets op een specifieke datum klaar is zonder dit te checken.',
  },
  {
    id: 'legal',
    icon: '⚖️',
    label: 'Juridische diensten',
    description: 'Advocatuur, mediation, juridisch advies',
    compiled_prompt: `Je bent een professionele assistent voor een juridisch kantoor. Mensen die contact opnemen bevinden zich vaak in een stressvolle situatie — behandel elk bericht met respect en empathie.

Je maakt nooit beloften over juridische uitkomsten. Je stelt altijd de aard van de zaak en de urgentie vast voordat je een afspraak plant. Complexe juridische vragen verwijs je altijd door naar de advocaat. Je spreekt altijd formeel tenzij de klant informeel is.

Je reageert kalm en helder. Geen haast, geen druk. Één vraag per bericht.`,
    operator_identity: 'Juridisch kantoor in Nederland. Specialisatie in [vul aan].',
    client_reading: 'Klanten die contact opnemen zijn vaak angstig of gestrest. Ze hebben behoefte aan rust en duidelijkheid, niet aan snelheid. Laat ze uitspreken. Stel de aard van de zaak vast voordat je iets adviseert.',
    signature_approach: 'Rustig, formeel, empathisch. Nooit neerbuigend. Geef ruimte. Bevestig dat je begrijpt wat er speelt voordat je een volgende stap noemt.',
    hard_constraints: 'Nooit een juridisch oordeel geven. Nooit zeggen dat iemand "gelijk heeft" of "ongelijk heeft". Altijd doorverwijzen naar de advocaat voor inhoudelijk advies.',
  },
  {
    id: 'fitness',
    icon: '🏋️',
    label: 'Fitness & coaching',
    description: 'Personal training, groepslessen, online coaching',
    compiled_prompt: `Je bent een enthousiaste assistent voor een personal training studio. Je helpt mensen met vragen over personal training, groepslessen en online coaching.

Je bent positief en motiverend zonder overdreven te zijn. Je vraagt altijd naar het doel van de klant (afvallen, spieren, conditie, gezondheid) en hun huidige ervaring voordat je een pakket aanraadt. Proeftrainingen plan je alleen als je de beschikbaarheid hebt gecheckt.

Je berichten zijn energiek maar bondig. Gebruik 'je' en 'jij', niet 'u'.`,
    operator_identity: 'Personal training studio in Nederland.',
    client_reading: 'Klanten die contact opnemen willen iets veranderen aan hun lichaam of gezondheid. Ze hebben vaak een trigger gehad — een medisch advies, een spiegelmoment, een vriend die begon. Sluit aan bij hun motivatie.',
    signature_approach: 'Positief, direct, persoonlijk. Gebruik voornamen. Kort en krachtig. Geen opsommingen tenzij het echt helpt.',
    hard_constraints: 'Nooit medisch advies geven. Nooit resultaten garanderen. Nooit een prijs noemen voor een pakket zonder te weten wat het doel is.',
  },
  {
    id: 'other',
    icon: '✏️',
    label: 'Andere branche',
    description: 'Stel Semmy zelf in',
    compiled_prompt: '',
    operator_identity: '',
    client_reading: '',
    signature_approach: '',
    hard_constraints: '',
  },
];

export function VerticalOnboarding({ partnerId, onComplete }: VerticalOnboardingProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSelect = async (verticalId: string) => {
    const vertical = VERTICALS.find(v => v.id === verticalId);
    if (!vertical) return;

    if (verticalId === 'other') {
      onComplete();
      return;
    }

    setSelected(verticalId);
    setSaving(true);

    const { error } = await supabase
      .from('semmy_operator_context')
      .upsert({
        partner_id: partnerId,
        compiled_prompt: vertical.compiled_prompt,
        operator_identity: vertical.operator_identity,
        client_reading: vertical.client_reading,
        signature_approach: vertical.signature_approach,
        hard_constraints: vertical.hard_constraints,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'partner_id' });

    setSaving(false);

    if (error) {
      console.error('VerticalOnboarding: upsert failed', error.message);
    }
    setDone(true);
  };

  useEffect(() => {
    if (!done) return;
    const t = setTimeout(onComplete, 2200);
    return () => clearTimeout(t);
  }, [done, onComplete]);

  const NEXT_STEPS = [
    { icon: '⚙️', text: 'Voeg je bedrijfsgegevens toe in Instellingen' },
    { icon: '💬', text: 'Test Semmy via de Chat' },
    { icon: '📥', text: 'Wacht op je eerste lead' },
  ];

  if (done) {
    return (
      <div style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
      }}>
        <div style={{
          width: '100%', maxWidth: '480px',
          background: 'var(--bg-surface-raised)', border: '1px solid var(--border-primary)',
          borderRadius: '16px', overflow: 'hidden', animation: 'fade-up 0.25s ease-out both',
          padding: '32px 28px',
        }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '50%',
            background: 'var(--accent-dim)', border: '1.5px solid var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '20px', marginBottom: '16px',
          }}>✓</div>
          <h2 style={{
            fontSize: '17px', fontWeight: '600', color: 'var(--text-primary)',
            marginBottom: '6px', letterSpacing: '-0.02em',
          }}>Semmy is klaar!</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-muted)', lineHeight: '1.5', marginBottom: '24px' }}>
            Een paar snelle stappen om het meeste uit Semmy te halen:
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {NEXT_STEPS.map((step, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: '14px',
                padding: '12px 14px', borderRadius: '10px',
                border: '1px solid var(--border-primary)', background: 'var(--bg-input)',
              }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>{step.icon}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}>
                  <span style={{
                    fontSize: '12px', fontWeight: '600', color: 'var(--accent)',
                    background: 'var(--accent-dim)', borderRadius: '4px',
                    padding: '1px 6px', flexShrink: 0,
                  }}>{i + 1}</span>
                  <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>{step.text}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      zIndex: 100,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: '480px',
        background: 'var(--bg-surface-raised)',
        border: '1px solid var(--border-primary)',
        borderRadius: '16px',
        overflow: 'hidden',
        animation: 'fade-up 0.25s ease-out both',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px',
          borderBottom: '1px solid var(--border-separator)',
        }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'var(--accent-dim)', border: '1.5px solid var(--accent)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '16px', fontWeight: '600', color: 'var(--accent)',
            marginBottom: '14px',
          }}>S</div>
          <h2 style={{
            fontSize: '17px',
            fontWeight: '600',
            color: 'var(--text-primary)',
            marginBottom: '6px',
            letterSpacing: '-0.02em',
          }}>
            Wat voor bedrijf heb je?
          </h2>
          <p style={{
            fontSize: '13px',
            color: 'var(--text-muted)',
            lineHeight: '1.5',
          }}>
            Semmy past haar aanpak aan op jouw branche. Je kunt alles later aanpassen in Instellingen.
          </p>
        </div>

        {/* Vertical options */}
        <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {VERTICALS.map(v => (
            <button
              key={v.id}
              onClick={() => !saving && handleSelect(v.id)}
              disabled={saving && selected !== v.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                padding: '14px 16px',
                borderRadius: '10px',
                border: `1px solid ${selected === v.id ? 'var(--accent)' : 'var(--border-primary)'}`,
                background: selected === v.id ? 'var(--accent-dim)' : 'var(--bg-input)',
                cursor: saving ? 'default' : 'pointer',
                textAlign: 'left',
                width: '100%',
                transition: 'border-color 0.15s, background 0.15s',
                opacity: saving && selected !== v.id ? 0.4 : 1,
              }}
            >
              <span style={{ fontSize: '22px', flexShrink: 0 }}>{v.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '500',
                  color: selected === v.id ? 'var(--accent)' : 'var(--text-primary)',
                  marginBottom: '2px',
                }}>
                  {v.label}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  {v.description}
                </div>
              </div>
              {saving && selected === v.id ? (
                <div style={{
                  width: '14px', height: '14px', borderRadius: '50%',
                  border: '2px solid var(--accent)',
                  borderTopColor: 'transparent',
                  animation: 'spin 0.8s linear infinite',
                  flexShrink: 0,
                }} />
              ) : (
                <div style={{
                  width: '16px', height: '16px', borderRadius: '50%',
                  border: `1.5px solid ${selected === v.id ? 'var(--accent)' : 'var(--border-primary)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {selected === v.id && (
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%',
                      background: 'var(--accent)',
                    }} />
                  )}
                </div>
              )}
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: '0 24px 20px' }}>
          <p style={{ fontSize: '11px', color: 'var(--text-muted)', textAlign: 'center' }}>
            Semmy is klaar om te beginnen zodra je een keuze maakt.
          </p>
        </div>
      </div>
    </div>
  );
}
